import React from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Typography,
  Skeleton,
  SkeletonContent,
  SkeletonBlock,
  SkeletonLine,
} from '@one-thd/sui-atomic-components';
import { GenericErrorMessage } from '../../../core/Constants';
import { useResponsive } from '../../../hooks';
import { useSweepstakesData } from '../../../contexts/SweepstakesDataContext';
import { SweepstakesMarkdown } from '../utils/SweepstakesMarkdown';

export const RenderSweepstakesDrawerLoading = () => (
  <div data-testid="sweepstakes-drawer-loading">
    <div className="sui-mb-6">
      <Skeleton grow disablePadding orientation="horizontal">
        <SkeletonContent disablePadding initialSize="1/4" grow>
          <SkeletonBlock aspect="wide" />
        </SkeletonContent>
        <SkeletonContent disablePadding initialSize="full" grow>
          <SkeletonLine numberOfLines={3} />
        </SkeletonContent>
      </Skeleton>
    </div>
    {[...Array(3)].map((val, index) => (
      <div key={index}>
        <Skeleton grow disablePadding="x">
          <SkeletonLine numberOfLines={3} />
        </Skeleton>
      </div>
    ))}
  </div>
);

export const RenderGenericError = ({ refresh, message = null, testId = null }) => (
  <div data-testid={testId || 'sweepstakes-drawer-error'}>
    <Alert status="error" size="large">
      <div>
        <Typography>
          {message || GenericErrorMessage}
          <span className="sui-mt-2 sui-block">
            <button
              type="button"
              className="sui-underline"
              onClick={refresh}
              aria-label="Retry accepting sweepstakes"
            >
              Refresh
            </button>
          </span>
        </Typography>
      </div>
    </Alert>
  </div>
);

export const RenderSweepstakesCardLoading = () => (
  <div data-testid="sweepstakes-loading-skeleton">
    <Skeleton orientation="horizontal" disablePadding grow>
      <SkeletonContent disablePadding initialSize="1/4" grow>
        <SkeletonBlock width={30} height={20} />
      </SkeletonContent>
      <SkeletonContent disablePadding initialSize="full">
        <SkeletonLine />
      </SkeletonContent>
    </Skeleton>
    <div className="sui-pt-6">
      <Skeleton disablePadding disableGutters grow>
        <div className="sui-block sm:sui-hidden">
          <SkeletonLine variant="multi" numberOfLines={3} fullWidth />
        </div>
        <div className="sui-hidden sm:sui-block">
          <SkeletonLine variant="multi" numberOfLines={2} fullWidth />
        </div>
      </Skeleton>
    </div>
  </div>
);

export const RenderCompletionAlert = () => {
  const { sweepstakesData } = useSweepstakesData();

  // Return null if no data or bonus entry not eligible
  if (!sweepstakesData || !sweepstakesData.bonusEntryEligible) {
    return <div data-testid="sweepstakes-completion-alert" />;
  }

  const {
    completedSteps = 0,
    totalSteps = 0,
    messageComplete,
    messageNotComplete,
  } = sweepstakesData;

  const isComplete = completedSteps === totalSteps && totalSteps > 0;

  // Determine message and background color
  const message = isComplete ? messageComplete : messageNotComplete;

  // If no message to display, return null
  if (!message) {
    return <div data-testid="sweepstakes-completion-alert" />;
  }

  return (
    <Alert data-testid="sweepstakes-completion-alert" size="small" status={isComplete ? 'success' : 'info'}>
      <div className="sui-flex-1" data-testid="completion-alert-message">
        <SweepstakesMarkdown markdownText={message} disableSpacing />
      </div>
    </Alert>
  );
};

RenderGenericError.propTypes = {
  refresh: PropTypes.func.isRequired,
};
